import React, { useEffect, useState } from 'react'
import { Axe, Ban, CircleCheckBig, Clock, Eye, Flag, Trash, TriangleAlert, UserCheck } from 'lucide-react'
import Filters from '../components/Filters';
import { useDispatch, useSelector } from 'react-redux';
import { GetContentCountAPI } from '../redux/actions/content-management/GetCountAction';
import { GetContentPostsAPI } from '../redux/actions/content-management/GetContentPosts';
import { formatDate, formatTime } from '../helpers/DateFormatter';
import backend_url, { image_url } from '../services/endpoints';
import { DeleteContentAPI } from '../redux/actions/content-management/DeleteContentAction';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import { deleteContentStateReset } from '../redux/slices/content-management/DeleteContentSlice';
import Swal from 'sweetalert2';
import { UpdateStatusAPI } from '../redux/actions/content-management/UpdateStatusAction';
import { updateStatusStateReset } from '../redux/slices/content-management/UpdateStatusSlice';
import { PulseLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';

const ContentManagement = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { response, loading, error } = useSelector((state) => state.contentCount);
    const { response: postsResponse, loading: postsLoading, error: postsError } = useSelector((state) => state.contentPosts);
    const { response: deleteContentResponse, loading: deleteContentLoading, error: deleteContentError } = useSelector((state) => state.deleteContent);
    const { response: updatePostStatusResponse, loading: updatePostStatusLoading, error: updatePostStatusError } = useSelector((state) => state.updatePostStatus);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(5);
    const [totalPages, setTotalPages] = useState(1);
    const [items, setItems] = useState(5);
    const [allPosts, setAllPosts] = useState([]);

    const [search, setSearch] = useState('');

    const [date, setDate] = useState('');

    const [status, setStatus] = useState('');

    const [searchInput, setSearchInput] = useState('');
    const [dateInput, setDateInput] = useState('');
    const [statusInput, setStatusInput] = useState('');

    const [statusUpdated, setStatusUpdated] = useState(false);

    const fetchContentCount = () => {
        dispatch(GetContentCountAPI());
    }

    // const fetchPosts = useCallback((p, l) => {
    //     dispatch(GetContentPostsAPI(p, l));
    // }, [dispatch]);

    const fetchPosts = (page, limit, search, date, status) => {
        dispatch(GetContentPostsAPI(page, limit, search, date, status));
    };

    const handleDeleteContent = async (id) => {
        try {
            Swal.fire({
                title: 'Are you sure?',
                text: `You want to delete`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        await dispatch(DeleteContentAPI(id));
                    } catch (error) {
                        Swal.fire(error?.response?.message);
                    }
                }
            });

        } catch (error) {
            showErrorToast('error while deleteing content')
        }
    }

    const handleStatusChange = async (id, status) => {
        try {
            const label = status === 'hard_ban' ? 'ban this user' : status === 'warn' ? 'warn this user' : 'mark this report reviewed';
            const swalOptions = {
                title: 'Are you sure?',
                text: `You want to ${label}.`,
                icon: status === 'reviewed' ? 'info' : 'warning',
                showCancelButton: true,
                confirmButtonColor: status === 'hard_ban' ? '#d33' : '#3085d6',
                cancelButtonColor: '#6c757d',
                confirmButtonText: status === 'hard_ban' ? 'Ban User' : status === 'warn' ? 'Warn User' : 'Review',
                cancelButtonText: 'Cancel',
            };

            if (status === 'hard_ban') {
                swalOptions.text = 'Select how long this user should be suspended.';
                swalOptions.input = 'select';
                swalOptions.inputOptions = {
                    '1_day': '1 Day',
                    '1_week': '1 Week',
                    '1_month': '1 Month',
                    permanent: 'Permanent',
                };
                swalOptions.inputValue = 'permanent';
            }

            const result = await Swal.fire(swalOptions);

            if (!result.isConfirmed) return;

            dispatch(UpdateStatusAPI({
                postId: id,
                action: status,
                suspension_duration: status === 'hard_ban' ? result.value || 'permanent' : undefined,
            }))
        } catch (error) {
            showErrorToast('error in updating status')
        }
    }

    const handleViewUser = (userId) => {
        if (!userId) {
            showErrorToast('User details not available');
            return;
        }
        navigate(`/admin-dashboard/user-detail/${userId}`, { state: { id: userId, from: '/admin-dashboard/content-management' } });
    };

    useEffect(() => {
        fetchContentCount();
    }, [postsResponse])

    useEffect(() => {
        fetchPosts(currentPage, limit, search, date, status);
    }, [currentPage, limit, search, date, statusUpdated, status])

    useEffect(() => {
        if (postsResponse) {
            const { totalPages, totalItems } = postsResponse.data;
            setTotalPages(totalPages);
            setItems(totalItems);
        }
    }, [postsResponse])

    useEffect(() => {
        if (postsResponse?.data?.filteredReports) {
            const newItems = postsResponse.data.filteredReports;

            setAllPosts(prev => {
                if (currentPage === 1) return newItems;

                // Filter out duplicates based on postId._id
                const uniqueItems = newItems.filter(
                    item => !prev.some(p => p?.postId?._id === item?.postId?._id)
                );
                return [...prev, ...uniqueItems];
            });

            setTotalPages(postsResponse.data.totalPages);
            setItems(postsResponse.data.totalItems);
        }
    }, [postsResponse]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY;
            const windowHeight = window.innerHeight;
            const fullHeight = document.documentElement.scrollHeight;

            if (scrollTop + windowHeight >= fullHeight - 10 && !postsLoading && currentPage < totalPages) {
                setCurrentPage(prev => prev + 1); // ✅ Increase page number
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [postsLoading, currentPage, totalPages]);

    useEffect(() => {
        if (statusUpdated) {
            setCurrentPage(1);        // re-fetch fresh data
            setAllPosts([]);          // clear existing posts before new fetch
            setStatusUpdated(false);  // reset flag for next updates
        }
    }, [statusUpdated]);

    useEffect(() => {
        if (deleteContentResponse?.status === true) {
            showSuccessToast(deleteContentResponse?.message);
            dispatch(deleteContentStateReset());
            setAllPosts(prev => prev.filter(
                post => post?.postId?._id !== deleteContentResponse?.data?.deletedPostId
            ));
            setCurrentPage(1); // ✅ Reset to reload fresh
        }
    }, [deleteContentResponse]);

    useEffect(() => {
        if (updatePostStatusResponse?.status === true) {
            dispatch(updateStatusStateReset());
            setStatusUpdated(true)
            showSuccessToast(updatePostStatusResponse?.message)
        }
    }, [updatePostStatusResponse])

    useEffect(() => {
        if (deleteContentError) {
            showErrorToast(deleteContentError?.data);
            dispatch(deleteContentStateReset())
        }
    }, [deleteContentError])

    useEffect(() => {
        if (updatePostStatusError) {
            showErrorToast(updatePostStatusError?.data);
            dispatch(updateStatusStateReset());
        }
    }, [updatePostStatusError])

    return (
        <>
            <section className='main-section common_section spacer'>
                <div className='container'>
                    <div className='row gy-4'>
                        <div className="col-12">
                            <div className="row gx-4 gy-4">
                                <div className="col-sm-6 col-md-4 col-xl-3">
                                    <div className="dash-num-info-cards">
                                        <div className="dash-num-info-cards-header">
                                            <div className="num-info-content-box">
                                                <h2>Total Reports</h2>
                                                <span className="num-values">{response?.data?.total_report}</span>
                                            </div>
                                            <div className="num-info-icon-box">
                                                <Flag />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-4 col-xl-3">
                                    <div className="dash-num-info-cards">
                                        <div className="dash-num-info-cards-header">
                                            <div className="num-info-content-box">
                                                <h2>Pending Review</h2>
                                                <span className="num-values">{response?.data?.pending_review}</span>
                                            </div>
                                            <div className="num-info-icon-box">
                                                <Clock />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-4 col-xl-3">
                                    <div className="dash-num-info-cards">
                                        <div className="dash-num-info-cards-header">
                                            <div className="num-info-content-box">
                                                <h2>Reviewed Today</h2>
                                                <span className="num-values">{response?.data?.reviewed_today}</span>
                                            </div>
                                            <div className="num-info-icon-box">
                                                <CircleCheckBig />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-4 col-xl-3">
                                    <div className="dash-num-info-cards">
                                        <div className="dash-num-info-cards-header">
                                            <div className="num-info-content-box">
                                                <h2>Actions Taken</h2>
                                                <span className="num-values">{response?.data?.action_taken}</span>
                                            </div>
                                            <div className="num-info-icon-box">
                                                <Axe />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Filters
                            searchInput={searchInput}
                            setSearchInput={setSearchInput}
                            dateInput={dateInput}
                            setDateInput={setDateInput}
                            statusInput={statusInput}
                            setStatusInput={setStatusInput}
                            setSearch={setSearch}
                            setDate={setDate}
                            setStatus={setStatus}
                            handleSearchClick={(searchValue, dateValue, statusValue) => {
                                if (searchValue === search && dateValue === date && statusValue === status) return;
                                setSearch(searchValue);
                                setDate(dateValue);
                                setStatus(statusValue);
                                setCurrentPage(1);
                                setAllPosts([]);
                            }}
                            handleClearClick={() => {
                                setCurrentPage(1);
                                setAllPosts([]);
                            }}
                        />
                        <div className='col-12'>
                            <div className="common-table-wrapper">
                                <div className="common-table-filter-wrapper">
                                    <div className="common-left-blk">
                                        <h2 className='fs-5 fw-bold mb-0'>Reported Content</h2>
                                    </div>
                                    {/* <div className="common-right-blk">
                                        <div className="common-sort-blk">
                                            <label className="d-flex align-items-center gap-2">Show:
                                                <select className="form-select">
                                                    <option value={5} selected>5</option>
                                                    <option value={10}>10</option>
                                                    <option value={15}>15</option>
                                                    <option value={20}>20</option>
                                                </select>
                                            </label>
                                        </div>
                                    </div> */}
                                </div>
                                {postsLoading && allPosts.length === 0 ? (
                                    <div style={{ minHeight: '260px' }} className='d-flex align-items-center justify-content-center'>
                                        <PulseLoader size={18} color="#0f0a30" />
                                    </div>
                                ) : allPosts?.length > 0 ?
                                    allPosts?.map((post, index) => {
                                        return (
                                            <div className='reported_content_block' key={index}>
                                                <div className='reported_content_item'>
                                                    <div className='reported_content_header hstack flex-wrap align-content-start gap-3'>
                                                        <div className='reported_user_img'>
                                                            <img src={post.reportedBy ? `${image_url}${post.reportedBy?.profilePic}` : ''} alt={post?.reportedBy && post?.reportedBy?.username} />
                                                        </div>
                                                        <div className='reported_user_detail'>
                                                            <div className='hstack flex-wrap align-content-start gap-3'>
                                                                <span className='fw-medium'>{`Reported by @${post.reportedBy ? post.reportedBy.username : ''}`} </span>
                                                                <span className='reported_on'>{post.createdAt ? formatDate(post.createdAt) : ''} at {post.createdAt ? formatTime(post.createdAt) : ''}</span>
                                                                <span className='reported_badge'>{post.status === 'reviewed' ? 'Reviewed' : (post.status || 'Open')}</span>
                                                            </div>
                                                            <div className='reported_reason_content'>
                                                                <span>Reason: {post.reason && post.reason}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='reported_post'>
                                                        <div className='reported_content_header hstack flex-wrap align-content-start gap-3'>
                                                            <div className='reported_user_img'>
                                                                <img src={post.postId?.userId ? `${image_url}${post?.postId?.userId?.profilePic}` : ''} alt={post?.postId?.userId && post?.postId?.userId?.username} />
                                                            </div>
                                                            <div className='reported_user_detail'>
                                                                <div className='hstack flex-wrap align-content-start gap-3'>
                                                                    <span className='fw-medium fs-14'>Reported User: @{post.postId.userId && post?.postId?.userId?.username}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <p className='py-2 fs-14 mb-2'>{post?.postId?.match && post?.postId?.match?.description}</p>
                                                        <div className='reported_post_media_block'>
                                                            {/* <p className='fs-14 fw-semibold text-white mb-0'>Reported Media Content</p> */}
                                                            <img src={post?.postId?.media ? `${image_url}${post?.postId?.media[0]?.url}` : ''} alt={post?.reason && post?.reason} />
                                                        </div>
                                                    </div>
                                                    <div className='reported_post_action_block'>
                                                        <button className='reported_post_action_btn' disabled={post.actionStatus === 'reviewed' || post.actionStatus === 'hard_ban'} onClick={() => handleStatusChange(post.postId._id, 'reviewed')}>
                                                            <span><Eye /></span>
                                                            <span>Review</span>
                                                        </button>
                                                        <button className='reported_post_action_btn' onClick={() => handleDeleteContent(post?.postId?._id)}>
                                                            {deleteContentLoading ? <PulseLoader color='#000000' /> :
                                                                <>
                                                                    <span><Trash /></span>
                                                                    <span>Delete Content</span>
                                                                </>}
                                                        </button>
                                                        <button className='reported_post_action_btn' onClick={() => handleViewUser(post?.postId?.userId?._id)}>
                                                            <span><UserCheck /></span>
                                                            <span>View User</span>
                                                        </button>
                                                        <button className='reported_post_action_btn' disabled={post.actionStatus === 'hard_ban'} onClick={() => handleStatusChange(post.postId._id, 'warn')}>
                                                            {updatePostStatusLoading ?
                                                                <PulseLoader color='#000000' /> :
                                                                <>
                                                                    <span><TriangleAlert /></span>
                                                                    <span>Warn User</span>
                                                                </>
                                                            }

                                                        </button>
                                                        <button className='reported_post_action_btn' disabled={post.actionStatus === 'hard_ban'} onClick={() => handleStatusChange(post.postId._id, 'hard_ban')}>
                                                            {updatePostStatusLoading ?
                                                                <PulseLoader color='#000000' /> :
                                                                <>
                                                                    <span><Ban /></span>
                                                                    <span>Ban User</span>
                                                                </>
                                                            }

                                                        </button>
                                                        {/* <button className='reported_post_action_btn pe-none user-select-none' disabled>
                                                            <span><Check /></span>
                                                            <span>Action Taken: User Banned</span>
                                                        </button> */}
                                                    </div>
                                                </div>

                                                {/* <h2 className='text-center'>No data available</h2> */}
                                            </div>
                                        )
                                    }
                                    )
                                    :
                                    (
                                        <h2 className='text-center'>No data available</h2>
                                    )
                                }

                            </div>
                        </div>
                    </div>
                </div>
            </section >
        </>
    )
}

export default ContentManagement
